import * as React from "react"
import { cn } from "@/lib/utils"
import { ProductImage } from "./ui/ProductImage"
import { Button } from "./ui/button"
import { EmailIcon, AlertIcon } from "./icons"

export interface PairingItemStolenErrorProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Product title (e.g., "NFC XP")
   */
  productTitle: string
  /**
   * Product image URL
   */
  productImage: string
  /**
   * NFC ID to display (read-only)
   */
  nfcId: string
  /**
   * Error message to display
   * @default "This item has been reported lost/stolen and is unusable. Please contact us."
   */
  errorMessage?: string
  /**
   * Contact email address
   * @default "contact@anyw3ar.com"
   */
  contactEmail?: string
}

/**
 * Pairing Item Stolen/Lost Error component
 *
 * Displays an error message when an NFC item has been reported as stolen or lost.
 * This component prevents pairing and directs the user to contact support.
 *
 * This component appears instead of PairingItemForm when the NFC item is flagged.
 *
 * @example
 * ```tsx
 * <PairingItemStolenError
 *   productTitle="NFC XP"
 *   productImage="/images/jacket.jpg"
 *   nfcId="adnoa46z5vr1z35ef"
 *   errorMessage="This item has been reported lost/stolen and is unusable. Please contact us."
 *   contactEmail="contact@anyw3ar.com"
 * />
 * ```
 */
export const PairingItemStolenError = React.forwardRef<
  HTMLDivElement,
  PairingItemStolenErrorProps
>(
  (
    {
      productTitle,
      productImage,
      nfcId,
      errorMessage = "This item has been reported lost/stolen and is unusable. Please contact us.",
      contactEmail = "contact@anyw3ar.com",
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-8 items-center p-8 box-border",
          className
        )}
        {...props}
      >
        {/* Title */}
        <div className="flex gap-4 items-center w-full">
          <p className="font-montserrat font-bold text-[0.9375rem] text-gray-900 text-center uppercase">
            Pairing {productTitle}
          </p>
        </div>

        {/* Product Image */}
        <div className="h-[12.6875rem] w-[11.9375rem] relative shrink-0">
          <ProductImage
            src={productImage}
            alt={productTitle}
            containerClassName="absolute inset-0"
          />
        </div>

        {/* NFC ID Display with Error Icon */}
        <div className="bg-white border border-gray-100 border-solid flex h-12 items-center justify-between px-4 rounded-[0.3125rem] w-full">
          <div className="flex flex-col gap-1 items-start w-[12.0625rem]">
            <div className="flex flex-col justify-end text-xs">
              <p className="font-montserrat font-normal leading-5 text-gray-500">
                NFC ID :
              </p>
            </div>
            <div className="flex flex-col justify-end text-[1.125rem] tracking-[0.25rem]">
              <p className="font-montserrat font-normal leading-5 text-gray-500 whitespace-nowrap">
                {nfcId}
              </p>
            </div>
          </div>
          <div className="flex gap-4 items-start px-0 py-2">
            <AlertIcon
              size={12}
              weight="bold"
              className="text-brand-red"
            />
          </div>
        </div>

        {/* Error Message */}
        <div className="flex flex-col gap-4 items-start w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-1 flex-col font-montserrat font-normal justify-end leading-[0] min-h-px min-w-px text-[0.9375rem] text-gray-500">
              <p className="leading-5 whitespace-pre-wrap">
                {errorMessage}
              </p>
            </div>
          </div>
        </div>

        {/* Contact Button */}
        <a href={`mailto:${contactEmail}`}>
          <Button
            variant="primary-dark"
            size="primary-dark"
            leftIcon={<EmailIcon size={12} />}
            className="h-10 w-[19.4375rem] rounded-[0.375rem] uppercase"
          >
            {contactEmail}
          </Button>
        </a>
      </div>
    )
  }
)

PairingItemStolenError.displayName = "PairingItemStolenError"
